/**
 * Service for interacting with the Merman WASM module
 */

export type GraphHandle = number;
export type DiagramTheme = 'default' | 'forest' | 'dark' | 'neutral' | 'base';

/**
 * Parse error with location information
 */
export class ParseError extends Error {
    constructor(
        public line: number,
        public column: number,
        message: string
    ) {
        super(message);
        this.name = 'ParseError';
    }
}

/**
 * Parse result from WASM
 */
interface ParseResultSuccess {
    success: true;
    handle: GraphHandle;
}

interface ParseResultError {
    success: false;
    line: number;
    column: number;
    message: string;
}

type ParseResult = ParseResultSuccess | ParseResultError;

interface MermanWasm {
    parse_mermaid(input: string): string;
    render_svg(handle: GraphHandle): string;
    render_svg_with_options(handle: GraphHandle): string;
    render_svg_with_theme(handle: GraphHandle, theme: DiagramTheme): string;
    render_pdf(handle: GraphHandle): Uint8Array;
    render_pdf_with_theme(handle: GraphHandle, theme: DiagramTheme): Uint8Array;
    free_graph(handle: GraphHandle): boolean;
    get_graph_count(): number;
    list_handles(): number[];
}

export class WasmService {
    private wasm: MermanWasm | null = null;
    private initPromise: Promise<void> | null = null;

    /**
     * Initialize the WASM module
     */
    async init(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = (async () => {
            try {
                // Dynamic import of the WASM module
                const wasmModule = await import('../../../pkg/merman_wasm.js');
                await wasmModule.default();
                this.wasm = wasmModule as unknown as MermanWasm;
                console.log('WASM module initialized');
            } catch (error) {
                console.error('Failed to initialize WASM module:', error);
                throw new Error('Failed to initialize WASM module');
            }
        })();

        return this.initPromise;
    }

    /**
     * Parse Mermaid diagram text and return a handle
     * @throws {ParseError} If parsing fails, with line and column information
     */
    parseMermaid(input: string): GraphHandle {
        if (!this.wasm) {
            throw new Error('WASM module not initialized');
        }

        const resultJson = this.wasm.parse_mermaid(input);
        const result: ParseResult = JSON.parse(resultJson);

        if (result.success) {
            return result.handle;
        } else {
            throw new ParseError(result.line, result.column, result.message);
        }
    }

    /**
     * Render a parsed graph as SVG
     */
    renderSvg(
        handle: GraphHandle,
        theme: DiagramTheme = 'default'
    ): string {
        if (!this.wasm) {
            throw new Error('WASM module not initialized');
        }
        return this.wasm.render_svg_with_theme(handle, theme);
    }

    /**
     * Render a parsed graph as PDF
     */
    renderPdf(handle: GraphHandle, theme: DiagramTheme = 'default'): Uint8Array {
        if (!this.wasm) {
            throw new Error('WASM module not initialized');
        }
        return this.wasm.render_pdf_with_theme(handle, theme);
    }

    /**
     * Free a graph handle
     */
    freeGraph(handle: GraphHandle): boolean {
        if (!this.wasm) {
            throw new Error('WASM module not initialized');
        }
        return this.wasm.free_graph(handle);
    }

    /**
     * Get the number of currently stored graphs
     */
    getGraphCount(): number {
        if (!this.wasm) {
            throw new Error('WASM module not initialized');
        }
        return this.wasm.get_graph_count();
    }

    /**
     * List all active handles
     */
    listHandles(): number[] {
        if (!this.wasm) {
            throw new Error('WASM module not initialized');
        }
        return this.wasm.list_handles();
    }
}
